import { useCallback, useEffect, useRef, useState } from 'react';
import { Flex, HStack, Icon } from '@chakra-ui/react';
import { Menu } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { AssessmentOutcome } from '@duke-hq/svc-api-db';
import { QuestionPill, SummaryPill } from './TableOfContentsPill';
import { useMarkingContext } from './context/MarkingContext';

function outcomeAsCorrect(outcome: AssessmentOutcome | null | undefined): boolean | null {
  if (outcome === AssessmentOutcome.SATISFACTORY) {
    return true;
  }

  if (outcome === AssessmentOutcome.NOT_YET_SATISFACTORY) {
    return false;
  }

  return null;
}

const HIDE_SCROLLBAR_STYLE = {
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none' as const,
  msOverflowStyle: 'none' as const,
};

const FADE = '1.5rem';

const fadeMask = (left: boolean, right: boolean) => {
  if (!left && !right) {
    return 'none';
  }

  const start = left ? `transparent, black ${FADE}` : 'black';
  const end = right ? `black calc(100% - ${FADE}), transparent` : 'black';

  return `linear-gradient(to right, ${start}, ${end})`;
};

export function TableOfContents() {
  const {
    assessmentSubmission,
    currentSlideIndex,
    maxVisitedIndex,
    isOverviewScreen,
    selectSlide,
    showOverview,
    canShowOverview,
  } = useMarkingContext();

  const assessorSlides = assessmentSubmission.assessorSlides;
  const summaryOutcomeCorrect = outcomeAsCorrect(assessmentSubmission.outcome);

  const tocRef = useRef<HTMLDivElement>(null);
  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);

  const updateFades = useCallback(() => {
    const el = tocRef.current;
    if (el == null) {
      return;
    }
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    setFadeLeft(el.scrollLeft > 1);
    setFadeRight(el.scrollLeft < maxScrollLeft - 1);
  }, []);

  useEffect(() => {
    const el = tocRef.current;
    if (el == null) {
      return undefined;
    }

    updateFades();
    const observer = new ResizeObserver(updateFades);
    observer.observe(el);

    return () => observer.disconnect();
  }, [updateFades, assessorSlides.length]);

  const onWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    const el = tocRef.current;
    const { deltaY, deltaX } = event;

    if (el == null || el.scrollWidth <= el.clientWidth || (deltaY === 0 && deltaX !== 0)) {
      return;
    }

    el.scrollLeft += deltaY + deltaX;
    event.preventDefault();
  };

  return (
    <HStack flex="1" minW="0" gap={3}>
      <Icon as={Menu} boxSize={iconSize.lg} color="fg.muted" aria-hidden />
      <Flex
        ref={tocRef}
        flex="1"
        minW="0"
        gap={2}
        overflowX="auto"
        onWheel={onWheel}
        onScroll={updateFades}
        css={{
          ...HIDE_SCROLLBAR_STYLE,
          maskImage: fadeMask(fadeLeft, fadeRight),
          WebkitMaskImage: fadeMask(fadeLeft, fadeRight),
        }}
      >
        {assessorSlides.map((slide, index) => (
          <QuestionPill
            key={slide.slideId}
            slide={slide}
            isSelected={!isOverviewScreen && index === currentSlideIndex}
            isDisabled={index > maxVisitedIndex}
            onClick={() => selectSlide(slide.slideId)}
          />
        ))}
        <SummaryPill
          isSelected={isOverviewScreen}
          isDisabled={!canShowOverview}
          outcomeCorrect={summaryOutcomeCorrect}
          onClick={showOverview}
        />
      </Flex>
    </HStack>
  );
}
